import React, { useState } from "react";
import PropTypes from "prop-types";
import withStyles from "@mui/styles/withStyles";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FeedbackDialog from "../FeedbackDialog";
import { parseJwtToken } from "../../../services/commons";
import { getAtmtFeedbackToken } from "../../../services/api";
import styles from "./styles";

const FeedbackTab = ({ classes = {} }) => {
  const { t } = useTranslation("common");
  const label = t("feedback");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [atmtAuth, setAtmtAuth] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useSelector((state) => state);

  const fetchToken = () => {
    // Guard against a second retry click firing while the previous request
    // is still in flight - without this, out-of-order responses could set
    // atmtAuth from a stale request or flip error/success back and forth.
    if (isLoading) return;
    setIsLoading(true);
    setHasError(false);
    getAtmtFeedbackToken()
      .then((data) => setAtmtAuth(data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("FeedbackTab: failed to fetch ATMT token", error);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  };

  const handleClick = () => {
    // Guard against duplicate in-flight requests from rapid clicks - the
    // dialog stays open (and the button is unreachable behind it) from the
    // first click until handleClose runs, so this is a reliable single gate.
    if (feedbackOpen) return;
    setFeedbackOpen(true);
    fetchToken();
  };

  const handleClose = () => {
    setFeedbackOpen(false);
    setAtmtAuth(null);
    setHasError(false);
    setIsLoading(false);
  };

  if (!auth || !auth.token) return null;

  const userId = (parseJwtToken(auth.token) || {}).userid || "";

  return (
    <>
      <button
        type="button"
        className={classes.tab}
        onClick={handleClick}
        aria-label={label}
        data-testid="feedback-tab"
      >
        <span className={classes.inner}>
          <ChatBubbleOutlineIcon className={classes.icon} />
          <span className={classes.label}>{label}</span>
        </span>
      </button>

      <Dialog open={feedbackOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <div className={classes.panel} data-testid="feedback-panel">
          <div className={classes.panelBody}>
            {atmtAuth ? (
              <FeedbackDialog atmtAuth={atmtAuth} userId={userId} />
            ) : hasError ? (
              <div className={classes.errorRoot} data-testid="feedback-error">
                <p className={classes.errorText}>{t("feedbackLoadError")}</p>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchToken}
                  disabled={isLoading}
                  data-testid="feedback-retry"
                >
                  {t("tryAgain")}
                </Button>
              </div>
            ) : (
              <div className={classes.loadingRoot} data-testid="feedback-loading">
                <div className={classes.spinner} />
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

FeedbackTab.propTypes = {
  classes: PropTypes.object,
};

export default withStyles(styles)(FeedbackTab);
